/**
 * Central Clerk session token attachment for API requests.
 * Clerk is the only auth source — no localStorage tokens.
 */
import { networkManager } from '../config/NetworkManager';

let tokenGetter = null;

const BACKEND_ORIGINS = new Set(
  [
    networkManager.EDS_BACKEND_URL,
    networkManager.CHECKPOINTS_URL,
    networkManager.QA_AGENT_URL,
    networkManager.ARTIFACTS_URL,
  ]
    .filter(Boolean)
    .map((base) => {
      try {
        return new URL(base).origin;
      } catch {
        return base;
      }
    })
);

export function setClerkTokenGetter(getter) {
  tokenGetter = typeof getter === 'function' ? getter : null;
}

export function isClerkTokenGetterRegistered() {
  return typeof tokenGetter === 'function';
}

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Wait until Clerk token getter is registered and returns a non-empty JWT.
 * Used before quota fetch so requests are not sent without Authorization.
 */
export async function waitForClerkSessionToken({
  maxAttempts = 20,
  delayMs = 125,
} = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (typeof tokenGetter === 'function') {
      try {
        const token = await tokenGetter();
        if (token) {
          return token;
        }
      } catch (err) {
        console.warn('[auth] tokenGetter failed:', err);
      }
    }
    await sleep(delayMs);
  }
  return null;
}

function isProtectedBackendUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (BACKEND_ORIGINS.has(parsed.origin)) {
      return true;
    }
    const path = parsed.pathname;
    return (
      path.startsWith('/query') ||
      path.includes('/generated-file') ||
      path.includes('/checkpoints') ||
      path.includes('/generation-quota') ||
      path.includes('/artifacts') ||
      path.includes('/reports') ||
      path.includes('/testcases')
    );
  } catch {
    return false;
  }
}

export async function getAuthHeaders(extra = {}) {
  const headers = { ...extra };
  if (!tokenGetter) {
    return headers;
  }
  try {
    const token = await tokenGetter();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    console.warn('[auth] Failed to obtain Clerk session token:', err);
  }
  return headers;
}

export async function authenticatedFetch(url, options = {}) {
  const incoming =
    options.headers instanceof Headers
      ? Object.fromEntries(options.headers.entries())
      : { ...(options.headers || {}) };
  const headers = await getAuthHeaders(incoming);

  if (tokenGetter && isProtectedBackendUrl(url) && !headers.Authorization) {
    console.error('[auth] Protected API call without Clerk session token:', url);
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401 && isProtectedBackendUrl(url)) {
    window.dispatchEvent(new CustomEvent('intdoc:auth-required'));
  }

  return response;
}
